/**
 * WhatsApp Number Validation Utility
 *
 * Validates phone numbers and checks if they could be WhatsApp numbers
 * Note: This is a client-side validation based on format only.
 * For production, consider using a WhatsApp Business API validation service.
 */

/**
 * Parse and clean phone number
 * @param {string} phoneNumber - The phone number to parse
 * @returns {object} - Parsed phone number components
 */
export const parsePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return { valid: false, cleaned: '', countryCode: '', number: '' };
  }

  // Remove all non-digit characters except +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');

  // Check if it starts with +
  const hasPlus = cleaned.startsWith('+');

  // Remove + for processing
  cleaned = cleaned.replace(/\+/g, '');

  // Must be at least 7 digits (minimum valid phone number)
  // and at most 15 digits (E.164 standard)
  if (cleaned.length < 7 || cleaned.length > 15) {
    return { valid: false, cleaned: '', countryCode: '', number: '' };
  }

  // Try to extract country code (1-3 digits at the start)
  let countryCode = '';
  let number = cleaned;

  // Common country codes (1-3 digits)
  // UAE: 971, USA: 1, UK: 44, India: 91, Saudi: 966, etc.
  const countryCodePatterns = [
    /^(971)(\d{9})$/, // UAE: +971 followed by 9 digits
    /^(966)(\d{9})$/, // Saudi Arabia: +966 followed by 9 digits
    /^(1)(\d{10})$/,  // USA/Canada: +1 followed by 10 digits
    /^(44)(\d{10})$/, // UK: +44 followed by 10 digits
    /^(91)(\d{10})$/, // India: +91 followed by 10 digits
    /^(20)(\d{10})$/, // Egypt: +20 followed by 10 digits
    /^(971|966|1|44|91|20|33|49|81|86|7|61|55|52|234|254|27)(\d+)$/ // Generic pattern
  ];

  let matched = false;
  for (const pattern of countryCodePatterns) {
    const match = cleaned.match(pattern);
    if (match) {
      countryCode = match[1];
      number = match[2];
      matched = true;
      break;
    }
  }

  // If no pattern matched, assume first 1-3 digits are country code
  if (!matched && cleaned.length > 7) {
    // Try extracting 1-3 digit country codes
    for (let i = 1; i <= 3 && i < cleaned.length - 7; i++) {
      countryCode = cleaned.substring(0, i);
      number = cleaned.substring(i);
      break;
    }
  }

  return {
    valid: true,
    cleaned: cleaned,
    countryCode: countryCode,
    number: number,
    formatted: hasPlus || countryCode ? `+${cleaned}` : cleaned,
    e164: `+${cleaned}` // E.164 format
  };
};

/**
 * Validate if a number could be a WhatsApp number
 * @param {string} phoneNumber - The phone number to validate
 * @returns {object} - Validation result
 */
export const validateWhatsAppNumber = (phoneNumber) => {
  const parsed = parsePhoneNumber(phoneNumber);

  if (!parsed.valid) {
    return {
      valid: false,
      error: 'Invalid phone number format. Please include country code (e.g., +971 50 123 4567)',
      formatted: null,
      suggestion: null
    };
  }

  // Check if country code is present
  if (!parsed.countryCode || parsed.countryCode.length === 0) {
    return {
      valid: false,
      error: 'Please include country code (e.g., +971 for UAE)',
      formatted: null,
      suggestion: `Try: +${parsed.cleaned}`
    };
  }

  // Number seems valid for WhatsApp
  return {
    valid: true,
    error: null,
    formatted: parsed.e164,
    countryCode: parsed.countryCode,
    nationalNumber: parsed.number,
    suggestion: null
  };
};

/**
 * Format phone number for display
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatWhatsAppNumber = (phoneNumber) => {
  const parsed = parsePhoneNumber(phoneNumber);

  if (!parsed.valid) {
    return phoneNumber;
  }

  // Format based on country code
  const { countryCode, number } = parsed;

  // UAE: +971 50 123 4567
  if (countryCode === '971') {
    return `+971 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
  }

  // USA: +1 (555) 123-4567
  if (countryCode === '1' && number.length === 10) {
    return `+1 (${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6)}`;
  }

  // Saudi: +966 50 123 4567
  if (countryCode === '966') {
    return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
  }

  // Generic format: +XX XXX XXX XXXX
  if (number.length >= 7) {
    const parts = number.match(/.{1,3}/g) || [number];
    return `+${countryCode} ${parts.join(' ')}`;
  }

  return parsed.e164;
};

/**
 * Get validation hint for WhatsApp number field
 * @param {string} phoneNumber - Current phone number value
 * @returns {string|null} - Hint message or null
 */
export const getWhatsAppHint = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.length === 0) {
    return 'Include country code (e.g., +971 50 123 4567)';
  }

  const validation = validateWhatsAppNumber(phoneNumber);

  if (!validation.valid && validation.suggestion) {
    return `Did you mean: ${validation.suggestion}?`;
  }

  if (validation.valid && validation.formatted) {
    return `Looks good! Format: ${formatWhatsAppNumber(phoneNumber)}`;
  }

  return null;
};

/**
 * Common country codes with their WhatsApp adoption
 */
export const COMMON_COUNTRY_CODES = [
  { code: '+971', country: 'UAE', flag: '🇦🇪', popular: true },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', popular: true },
  { code: '+20', country: 'Egypt', flag: '🇪🇬', popular: true },
  { code: '+91', country: 'India', flag: '🇮🇳', popular: true },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸', popular: true },
  { code: '+44', country: 'UK', flag: '🇬🇧', popular: true },
  { code: '+974', country: 'Qatar', flag: '🇶🇦', popular: true },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼', popular: true },
  { code: '+968', country: 'Oman', flag: '🇴🇲', popular: true },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭', popular: true },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧', popular: true },
  { code: '+962', country: 'Jordan', flag: '🇯🇴', popular: true },
  { code: '+963', country: 'Syria', flag: '🇸🇾', popular: false },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰', popular: true },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩', popular: true },
  { code: '+94', country: 'Sri Lanka', flag: '🇱🇰', popular: true },
  { code: '+977', country: 'Nepal', flag: '🇳🇵', popular: true },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬', popular: true },
  { code: '+27', country: 'South Africa', flag: '🇿🇦', popular: true },
  { code: '+254', country: 'Kenya', flag: '🇰🇪', popular: true },
  { code: '+63', country: 'Philippines', flag: '🇵🇭', popular: true },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩', popular: true },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', popular: true },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', popular: true },
  { code: '+66', country: 'Thailand', flag: '🇹🇭', popular: true },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳', popular: true }
];

export default {
  parsePhoneNumber,
  validateWhatsAppNumber,
  formatWhatsAppNumber,
  getWhatsAppHint,
  COMMON_COUNTRY_CODES
};
